<template>
  <div :class="wrapperClass">
    <input
      ref="inputEl"
      :value="modelValue"
      :disabled="disabled"
      type="text"
      v-bind="$attrs"
      @input="emit('update:modelValue', $event.target.value)"
      @keydown.enter="emit('enter')"
    />
    <!-- Accent bar: works on every device, so no Vietnamese keyboard needed.
         Letter buttons insert at the caret; tone buttons accent the vowel
         immediately before the caret. -->
    <div v-if="!disabled" class="mt-2 flex flex-wrap items-center gap-1.5">
      <button
        v-for="ch in letterKeys"
        :key="ch"
        type="button"
        :aria-label="`Insert ${ch}`"
        @pointerdown.prevent="insertChar(ch)"
        class="min-h-[2.75rem] min-w-[2.75rem] px-3 rounded-lg border border-gray-200 bg-white text-lg font-medium text-gray-700 hover:bg-emerald-50 hover:border-emerald-300 active:bg-emerald-100 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-600"
      >{{ ch }}</button>
      <span class="w-px self-stretch bg-gray-200 dark:bg-gray-600 mx-1"></span>
      <button
        v-for="tone in toneKeys"
        :key="tone.combining"
        type="button"
        :aria-label="tone.aria"
        @pointerdown.prevent="applyTone(tone.combining)"
        class="min-h-[2.75rem] min-w-[2.75rem] px-3 rounded-lg border border-gray-200 bg-white text-lg font-medium text-gray-700 hover:bg-emerald-50 hover:border-emerald-300 active:bg-emerald-100 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-600"
      >{{ tone.label }}</button>

      <!-- How-to opener: small, low-emphasis. Re-opens the popup any time. -->
      <button
        type="button"
        @pointerdown.prevent="openHelp"
        class="ml-auto inline-flex items-center gap-1 self-center text-xs font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
      >
        <span aria-hidden="true" class="inline-flex h-4 w-4 items-center justify-center rounded-full border border-current text-[0.625rem] leading-none">?</span>
        How to use this
      </button>
    </div>

    <!-- Help popup: explains the accent bar with live examples of the buttons.
         Shown automatically the first time the input ever appears, then only
         when the learner taps "How to use this". -->
    <Teleport to="body">
      <div
        v-if="showHelp"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
        @click.self="closeHelp"
      >
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 text-left max-h-[90vh] overflow-y-auto">
          <div class="flex items-start justify-between gap-3">
            <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">Typing Vietnamese</h2>
            <button
              type="button"
              @click="closeHelp"
              aria-label="Close"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none"
            >&times;</button>
          </div>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
            No special keyboard needed. Type with your normal keyboard, then use the bar
            below the box to add Vietnamese letters and tones — on any device.
          </p>

          <!-- Letters -->
          <div class="mt-5">
            <div class="flex items-center gap-2">
              <span class="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-gray-200 bg-white text-lg font-medium text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100">ư</span>
              <p class="text-sm text-gray-600 dark:text-gray-300">
                <span class="font-semibold text-gray-900 dark:text-gray-100">Letter keys</span>
                insert a Vietnamese letter at your cursor.
              </p>
            </div>
          </div>

          <!-- Tones -->
          <div class="mt-4">
            <div class="flex items-center gap-2">
              <span class="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-gray-200 bg-white text-lg font-medium text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100">á</span>
              <p class="text-sm text-gray-600 dark:text-gray-300">
                <span class="font-semibold text-gray-900 dark:text-gray-100">Tone keys</span>
                add a tone mark to the vowel just <em>before</em> your cursor.
              </p>
            </div>
          </div>

          <!-- Worked example -->
          <div class="mt-5 rounded-xl bg-gray-50 dark:bg-gray-700/50 p-4 text-sm text-gray-600 dark:text-gray-300">
            <p class="font-semibold text-gray-900 dark:text-gray-100 mb-1">Example: type “từ”</p>
            <ol class="list-decimal list-inside space-y-1">
              <li>Type <code class="font-mono">t</code> on your keyboard</li>
              <li>Tap <span class="font-mono px-1 rounded border border-gray-200 dark:border-gray-600">ư</span> to add the letter</li>
              <li>Tap <span class="font-mono px-1 rounded border border-gray-200 dark:border-gray-600">à</span> to put the tone on it → <span class="font-mono">từ</span></li>
            </ol>
          </div>

          <button
            type="button"
            @click="closeHelp"
            class="mt-6 w-full rounded-xl bg-emerald-600 text-white px-5 py-2.5 font-medium hover:bg-emerald-700"
          >Got it</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted } from 'vue'

// Forward placeholder / class / aria etc. straight to the <input>.
defineOptions({ inheritAttrs: false })

// "How to use this" popup. Auto-shown the first time the input ever appears
// (tracked in localStorage), then only when the learner taps the opener.
const HELP_SEEN_KEY = 'speaksaigon_viet_input_help_seen'
const showHelp = ref(false)

function helpAlreadySeen() {
  try { return localStorage.getItem(HELP_SEEN_KEY) === 'true' } catch (e) { return false }
}
function markHelpSeen() {
  try { localStorage.setItem(HELP_SEEN_KEY, 'true') } catch (e) { /* private mode: just skip */ }
}
function openHelp() {
  showHelp.value = true
}
function closeHelp() {
  showHelp.value = false
  markHelpSeen()
}

onMounted(() => {
  // Only auto-open on a usable (enabled) input, and only the very first time.
  if (!props.disabled && !helpAlreadySeen()) {
    showHelp.value = true
    markHelpSeen()
  }
})

const props = defineProps({
  modelValue: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  // Classes for the outer wrapper (e.g. layout like flex-1). The `class`
  // attribute itself falls through to the <input>.
  wrapperClass: { type: [String, Array, Object], default: '' },
})
const emit = defineEmits(['update:modelValue', 'enter'])

const inputEl = ref(null)

// Modified base letters that don't exist on a standard English keyboard.
const letterKeys = ['â', 'ă', 'ê', 'ô', 'ơ', 'ư', 'đ']

// The five Southern tones, applied as combining marks. Labels show the mark on
// "a" so learners recognise them; the mark itself works on any vowel.
const toneKeys = [
  { label: 'à', combining: '̀', aria: 'Grave tone (huyền)' },
  { label: 'á', combining: '́', aria: 'Acute tone (sắc)' },
  { label: 'ả', combining: '̉', aria: 'Hook tone (hỏi)' },
  { label: 'ã', combining: '̃', aria: 'Tilde tone (ngã)' },
  { label: 'ạ', combining: '̣', aria: 'Dot tone (nặng)' },
]
// Tone combining marks only — letter modifications (circumflex, breve, horn)
// are intentionally preserved.
const TONE_MARKS = /[\u0300\u0301\u0303\u0309\u0323]/g

function caret() {
  const el = inputEl.value
  const len = props.modelValue.length
  return {
    start: el?.selectionStart ?? len,
    end: el?.selectionEnd ?? len,
  }
}

function commit(next, caretPos) {
  emit('update:modelValue', next)
  nextTick(() => {
    const el = inputEl.value
    if (!el) return
    el.focus()
    el.setSelectionRange(caretPos, caretPos)
  })
}

function insertChar(ch) {
  const { start, end } = caret()
  const v = props.modelValue
  commit(v.slice(0, start) + ch + v.slice(end), start + ch.length)
}

function applyTone(combining) {
  const { start, end } = caret()
  if (start === 0) return
  const v = props.modelValue
  const before = v.slice(0, start)
  const after = v.slice(end)
  // Operate on the character immediately before the caret.
  const chars = [...before]
  const lastChar = chars[chars.length - 1]
  const head = before.slice(0, before.length - lastChar.length)
  // Strip any existing tone, then add the new one and recompose to NFC so we
  // get a single precomposed code point (works for ă/â/ê/ô/ơ/ư too).
  const toned = (lastChar.normalize('NFD').replace(TONE_MARKS, '') + combining).normalize('NFC')
  commit(head + toned + after, (head + toned).length)
}
</script>
